import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { patch, post } from "../lib/api";
import { diffPatch } from "../lib/format";
import { SIGNAL_TYPES } from "../lib/leads";
import { todayISO } from "../../helpers/datetime";
import { Button, Checkbox, Chip, Field, Input, Modal, Spinner, Textarea } from "../ui";
import EntityPicker from "../components/EntityPicker";
import { useShell } from "./context";

const KEYS = ["company_id", "type", "seen_on", "source_url", "detail", "acted_on"];

const blank = (p = {}) => {
    const s = p.signal;
    if (s) {
        return { company: { id: s.company_id, name: s.company_name }, type: s.type, seen_on: s.seen_on, source_url: s.source_url || "", detail: s.detail || "", acted_on: !!s.acted_on };
    }
    return { company: p.company || null, type: p.type || "", seen_on: todayISO(), source_url: "", detail: "", acted_on: false };
};

// "Add signal" (or edit one): a buying signal at a CRM company. The same
// company, type, date seen and URL as a live signal is a 409 duplicate.
export default function SignalModal() {
    const { signalState, closeSignal, toast, bump } = useShell();
    const history = useHistory();
    const prefill = (signalState && signalState.prefill) || {};
    const editing = prefill.signal;
    const [f, setF] = useState(blank());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [existing, setExisting] = useState(null);

    useEffect(() => {
        if (!signalState) return;
        setF(blank(signalState.prefill));
        setError(null);
        setExisting(null);
        setBusy(false);
    }, [signalState]);

    if (!signalState) return null;
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v && v.target ? v.target.value : v }));
    const today = todayISO();

    const save = async () => {
        if (!f.company) return setError("Pick the company the signal is about.");
        if (!f.type) return setError("Pick a signal type.");
        const url = f.source_url.trim();
        if (url && !/^https?:\/\//i.test(url)) return setError("The source URL must start with http:// or https://.");
        setBusy(true);
        setError(null);
        setExisting(null);
        const body = { company_id: f.company.id, type: f.type, seen_on: f.seen_on || today, source_url: url, detail: f.detail.trim(), acted_on: f.acted_on };
        try {
            let res;
            if (editing) {
                const changes = diffPatch(editing, body, KEYS);
                res = Object.keys(changes).length ? await patch(`v1/crm/signals/${editing.id}`, changes) : editing;
                toast("Signal saved.");
            } else {
                if (!body.source_url) delete body.source_url;
                if (!body.detail) delete body.detail;
                if (!body.acted_on) delete body.acted_on;
                res = await post("v1/crm/signals", body);
                toast(`Signal added at ${res.company_name}.`, { icon: "ri-radar-line" });
            }
            bump();
            closeSignal();
            if (prefill.onSaved) prefill.onSaved(res);
        } catch (e) {
            setBusy(false);
            if (e.status === 409) {
                setError("This signal is already recorded: same company, type, date seen and source URL.");
                if (e.data && e.data.existing_id) setExisting({ id: e.data.existing_id, company_id: f.company.id });
            } else {
                setError(e.message);
            }
        }
    };

    const showExisting = () => {
        closeSignal();
        history.push(`/crm/signals?company_id=${existing.company_id}&highlight=${existing.id}`);
    };

    return (
        <Modal
            open
            title={editing ? "Edit signal" : "Add signal"}
            onClose={closeSignal}
            width={540}
            footer={
                <>
                    <Button variant="light" onClick={closeSignal}>Cancel</Button>
                    <Button variant="primary" onClick={save} disabled={busy}>
                        {busy && <Spinner />}
                        {editing ? "Save" : "Add signal"}
                    </Button>
                </>
            }
        >
            <div className="hz-form-grid" onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && save()}>
                {error && (
                    <div className="hz-alert hz-alert-danger">
                        {error}
                        {existing && (
                            <>
                                {" "}
                                <button type="button" className="hz-btn-link" onClick={showExisting}>Show the existing signal</button>
                            </>
                        )}
                    </div>
                )}
                <div>
                    <span className="hz-label">Company<span className="req"> *</span></span>
                    <EntityPicker kind="company" value={f.company} onChange={set("company")} autoFocus={!f.company} />
                    <div className="hz-hint">Signals are kept on CRM companies. Add the company first if it is new.</div>
                </div>
                <div>
                    <span className="hz-label">Type<span className="req"> *</span></span>
                    <div className="hz-row-wrap" style={{ gap: 6 }}>
                        {SIGNAL_TYPES.map((t) => (
                            <Chip key={t.value} icon={t.icon} active={f.type === t.value} onClick={() => set("type")(t.value)}>
                                {t.label}
                            </Chip>
                        ))}
                    </div>
                </div>
                <div className="hz-form-2" style={{ gridTemplateColumns: "160px 1fr" }}>
                    <Field label="Seen on">
                        <Input type="date" value={f.seen_on} max={today} onChange={set("seen_on")} />
                    </Field>
                    <Field label="Source URL">
                        <Input value={f.source_url} onChange={set("source_url")} placeholder="https://" />
                    </Field>
                </div>
                <Field label="Detail">
                    <Textarea rows={3} value={f.detail} onChange={set("detail")} placeholder="Series B, 20M EUR; new CTO from…" />
                </Field>
                {editing && <Checkbox checked={f.acted_on} onChange={set("acted_on")}>Acted on</Checkbox>}
            </div>
        </Modal>
    );
}
